// Global Styles for WhatsApp Portal - Professional Design System
// Includes CSS reset, utility classes, and global design tokens

import { keyframes } from './animations';
import { spacingCssVariables } from './spacing';

// Global CSS styles to be injected
export const globalStyles = `
  /* CSS Reset and Normalization */
  *, *::before, *::after {
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-text-size-adjust: 100%;
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.6;
    color: #212121;
    background-color: #FAFAFA;
    overflow-x: hidden;
  }

  /* High-quality font rendering */
  body, input, textarea, button {
    font-feature-settings: 'kern' 1, 'liga' 1;
    text-rendering: optimizeLegibility;
  }

  /* Remove default margins and paddings */
  h1, h2, h3, h4, h5, h6, p, ul, ol, li, blockquote, figure {
    margin: 0;
    padding: 0;
  }

  /* List reset */
  ul, ol {
    list-style: none;
  }

  /* Link reset */
  a {
    color: inherit;
    text-decoration: none;
  }

  /* Button reset */
  button {
    border: none;
    background: none;
    font: inherit;
    cursor: pointer;
  }

  /* Image optimization */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* CSS Custom Properties (Design Tokens) */
  :root {
    /* Spacing tokens */
    ${Object.entries(spacingCssVariables).map(([key, value]) => `${key}: ${value};`).join('\n    ')}
    
    /* Color tokens */
    --color-primary-50: #E8F5E8;
    --color-primary-100: #C8E6C9;
    --color-primary-500: #25D366;
    --color-primary-600: #21C05D;
    --color-primary-700: #1BAA52;
    --color-primary-900: #0D7332;
    
    --color-secondary-500: #128C7E;
    --color-secondary-600: #00796B;
    --color-secondary-700: #00695C;
    
    --color-gray-50: #FAFAFA;
    --color-gray-100: #F5F5F5;
    --color-gray-200: #EEEEEE;
    --color-gray-300: #E0E0E0;
    --color-gray-400: #BDBDBD;
    --color-gray-500: #9E9E9E;
    --color-gray-600: #757575;
    --color-gray-700: #616161;
    --color-gray-800: #424242;
    --color-gray-900: #212121;
    
    --color-success-500: #4CAF50;
    --color-warning-500: #FF9800;
    --color-error-500: #F44336;
    --color-info-500: #2196F3;
    
    --color-text-primary: #212121;
    --color-text-secondary: #757575;
    --color-text-tertiary: #9E9E9E;
    --color-text-disabled: #BDBDBD;
    
    --color-surface-background: #FAFAFA;
    --color-surface-paper: #FFFFFF;
    --color-surface-elevated: #FFFFFF;
    
    --color-border-light: #F5F5F5;
    --color-border-medium: #E0E0E0;
    --color-border-dark: #BDBDBD;
    
    /* Typography tokens */
    --font-family-primary: 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif;
    --font-family-mono: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    
    --font-weight-light: 300;
    --font-weight-regular: 400;
    --font-weight-medium: 500;
    --font-weight-semibold: 600;
    --font-weight-bold: 700;
    
    --line-height-tight: 1.25;
    --line-height-normal: 1.5;
    --line-height-relaxed: 1.75;
    
    --letter-spacing-tight: -0.025em;
    --letter-spacing-normal: 0;
    --letter-spacing-wide: 0.025em;
    
    /* Shadow tokens */
    --shadow-1: 0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24);
    --shadow-2: 0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23);
    --shadow-3: 0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23);
    --shadow-4: 0px 14px 28px rgba(0, 0, 0, 0.25), 0px 10px 10px rgba(0, 0, 0, 0.22);
    
    /* Border radius tokens */
    --radius-small: 4px;
    --radius-medium: 8px;
    --radius-large: 12px;
    --radius-xlarge: 16px;
    --radius-round: 50%;
    
    /* Animation tokens */
    --duration-instant: 100ms;
    --duration-fast: 150ms;
    --duration-quick: 200ms;
    --duration-standard: 300ms;
    --duration-moderate: 400ms;
    --duration-comfortable: 500ms;
    
    --easing-linear: cubic-bezier(0, 0, 1, 1);
    --easing-ease-out: cubic-bezier(0, 0, 0.2, 1);
    --easing-ease-in: cubic-bezier(0.4, 0, 1, 1);
    --easing-ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);
    --easing-professional: cubic-bezier(0.25, 0.1, 0.25, 1);
    
    /* Z-index tokens */
    --z-index-dropdown: 1000;
    --z-index-sticky: 1020;
    --z-index-fixed: 1030;
    --z-index-modal-backdrop: 1040;
    --z-index-modal: 1050;
    --z-index-popover: 1060;
    --z-index-tooltip: 1070;
    --z-index-toast: 1080;
  }

  /* Animation Keyframes */
  ${Object.values(keyframes).join('\n  ')}

  /* Utility Classes */
  
  /* Spacing utilities */
  .m-0 { margin: 0; }
  .m-1 { margin: var(--spacing-1); }
  .m-2 { margin: var(--spacing-2); }
  .m-3 { margin: var(--spacing-3); }
  .m-4 { margin: var(--spacing-4); }
  .m-5 { margin: var(--spacing-5); }
  .m-6 { margin: var(--spacing-6); }
  .m-8 { margin: var(--spacing-8); }
  
  .p-0 { padding: 0; }
  .p-1 { padding: var(--spacing-1); }
  .p-2 { padding: var(--spacing-2); }
  .p-3 { padding: var(--spacing-3); }
  .p-4 { padding: var(--spacing-4); }
  .p-5 { padding: var(--spacing-5); }
  .p-6 { padding: var(--spacing-6); }
  .p-8 { padding: var(--spacing-8); }
  
  /* Text utilities */
  .text-primary { color: var(--color-text-primary); }
  .text-secondary { color: var(--color-text-secondary); }
  .text-tertiary { color: var(--color-text-tertiary); }
  .text-disabled { color: var(--color-text-disabled); }
  
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }
  
  .font-light { font-weight: var(--font-weight-light); }
  .font-regular { font-weight: var(--font-weight-regular); }
  .font-medium { font-weight: var(--font-weight-medium); }
  .font-semibold { font-weight: var(--font-weight-semibold); }
  .font-bold { font-weight: var(--font-weight-bold); }
  
  /* Display utilities */
  .block { display: block; }
  .inline { display: inline; }
  .inline-block { display: inline-block; }
  .flex { display: flex; }
  .inline-flex { display: inline-flex; }
  .grid { display: grid; }
  .hidden { display: none; }
  
  /* Flexbox utilities */
  .flex-col { flex-direction: column; }
  .flex-row { flex-direction: row; }
  .flex-wrap { flex-wrap: wrap; }
  .flex-nowrap { flex-wrap: nowrap; }
  
  .justify-start { justify-content: flex-start; }
  .justify-center { justify-content: center; }
  .justify-end { justify-content: flex-end; }
  .justify-between { justify-content: space-between; }
  .justify-around { justify-content: space-around; }
  
  .items-start { align-items: flex-start; }
  .items-center { align-items: center; }
  .items-end { align-items: flex-end; }
  .items-stretch { align-items: stretch; }
  
  /* Border radius utilities */
  .rounded-none { border-radius: 0; }
  .rounded-sm { border-radius: var(--radius-small); }
  .rounded { border-radius: var(--radius-medium); }
  .rounded-lg { border-radius: var(--radius-large); }
  .rounded-xl { border-radius: var(--radius-xlarge); }
  .rounded-full { border-radius: var(--radius-round); }
  
  /* Shadow utilities */
  .shadow-none { box-shadow: none; }
  .shadow-1 { box-shadow: var(--shadow-1); }
  .shadow-2 { box-shadow: var(--shadow-2); }
  .shadow-3 { box-shadow: var(--shadow-3); }
  .shadow-4 { box-shadow: var(--shadow-4); }
  
  /* Animation utilities */
  .transition-all {
    transition: all var(--duration-standard) var(--easing-ease-in-out);
  }
  
  .transition-colors {
    transition: color var(--duration-fast) var(--easing-ease-out),
                background-color var(--duration-fast) var(--easing-ease-out),
                border-color var(--duration-fast) var(--easing-ease-out);
  }
  
  .transition-transform {
    transition: transform var(--duration-standard) var(--easing-professional);
  }
  
  .transition-opacity {
    transition: opacity var(--duration-standard) var(--easing-ease-in-out);
  }
  
  /* Loading animations */
  .loading-shimmer {
    background: linear-gradient(
      90deg,
      var(--color-gray-100) 25%,
      var(--color-gray-200) 50%,
      var(--color-gray-100) 75%
    );
    background-size: 200% 100%;
    animation: shimmer var(--duration-loading) var(--easing-linear) infinite;
  }
  
  .loading-pulse {
    animation: pulse var(--duration-heartbeat) var(--easing-ease-in-out) infinite;
  }
  
  .loading-spin {
    animation: rotate var(--duration-slow) var(--easing-linear) infinite;
  }
  
  /* Interactive states */
  .hover-lift {
    transition: transform var(--duration-fast) var(--easing-ease-out),
                box-shadow var(--duration-fast) var(--easing-ease-out);
  }
  
  .hover-lift:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-2);
  }
  
  .hover-scale {
    transition: transform var(--duration-fast) var(--easing-ease-out);
  }
  
  .hover-scale:hover {
    transform: scale(1.05);
  }
  
  /* Focus styles for accessibility */
  .focus-ring:focus {
    outline: 2px solid var(--color-primary-500);
    outline-offset: 2px;
  }
  
  .focus-ring:focus:not(:focus-visible) {
    outline: none;
  }
  
  /* Accessibility utilities */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  .not-sr-only {
    position: static;
    width: auto;
    height: auto;
    padding: 0;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
  }
  
  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
  
  /* Print styles */
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
    }
    
    a, a:visited {
      text-decoration: underline;
    }
    
    a[href]:after {
      content: " (" attr(href) ")";
    }
    
    abbr[title]:after {
      content: " (" attr(title) ")";
    }
    
    .no-print {
      display: none !important;
    }
  }
  
  /* High contrast mode support */
  @media (prefers-contrast: high) {
    :root {
      --color-text-primary: #000000;
      --color-text-secondary: #000000;
      --color-border-medium: #000000;
    }
  }
  
  /* Dark mode preparation (for future implementation) */
  @media (prefers-color-scheme: dark) {
    :root {
      --color-surface-background: #121212;
      --color-surface-paper: #1E1E1E;
      --color-text-primary: #FFFFFF;
      --color-text-secondary: #BDBDBD;
    }
  }

  /* Custom scrollbar for webkit browsers */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: var(--color-gray-100);
    border-radius: var(--radius-small);
  }
  
  ::-webkit-scrollbar-thumb {
    background: var(--color-gray-400);
    border-radius: var(--radius-small);
    transition: background-color var(--duration-fast) var(--easing-ease-out);
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: var(--color-gray-500);
  }
  
  /* Selection styles */
  ::selection {
    background-color: rgba(37, 211, 102, 0.2);
    color: var(--color-text-primary);
  }
  
  ::-moz-selection {
    background-color: rgba(37, 211, 102, 0.2);
    color: var(--color-text-primary);
  }
`;

export default globalStyles;